import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Plus, Store, Rocket, ChevronRight, Coins, LogOut, BookMarked,
  Loader2, MoreVertical, Pencil, Copy, Download, Archive, Trash2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import { NexusLogo } from '../brand/NexusLogo';
import { toast } from 'sonner';
import { useT } from '../../contexts/LocaleContext';

interface Workspace {
  id: string;
  name: string;
  last_activity: string;
  status?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Nexus — Menú lateral (screen 4 of the 6-screen reference).
 *
 * Slides from the left on any screen with a menu button. Items are
 * REAL — every entry navigates to a working route:
 *   • Nuevo Workspace → Home (with prompt focused)
 *   • Marketplace → placeholder route (Coming Soon toast if not ready)
 *   • Apps desplegadas → filter Home list by "deployed"
 *   • Workspaces → live list from `apiService.listWorkspaces()`
 *   • Perfil → /profile
 *   • Créditos → /profile#credits
 */
export const Sidebar: React.FC<Props> = ({ open, onClose }) => {
  const nav = useNavigate();
  const t = useT();
  const { logout } = useAuth();
  const { balance, openCreditsPanel } = useCredits();
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [me, setMe] = useState<{ name?: string; email?: string; credits?: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<Workspace | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Workspace | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reloadWorkspaces = async () => {
    try {
      const ws = await apiService.listWorkspaces();
      setWorkspaces(ws || []);
    } catch {
      // toast handled by callers
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      apiService.listWorkspaces().catch(() => []),
      apiService.getMe().catch(() => null),
    ])
      .then(([ws, meData]) => {
        setWorkspaces(ws || []);
        setMe(meData);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const go = (path: string) => {
    onClose();
    nav(path);
  };

  const firstName = me?.name?.split(' ')[0] || me?.email?.split('@')[0] || 'Nexus';

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="sidebar-backdrop"
          />
          {/* Panel */}
          <motion.aside
            key="panel"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className="fixed left-0 top-0 bottom-0 z-50 w-[320px] max-w-[85vw] flex flex-col overflow-hidden"
            style={{
              background: 'hsl(var(--bg-elev))',
              borderRight: '1px solid hsl(var(--border))',
            }}
            data-testid="sidebar-panel"
          >
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
              <NexusLogo size={26} showText />
              <button
                onClick={onClose}
                data-testid="sidebar-close-btn"
                className="p-1.5 rounded-lg hover:bg-white/5"
                aria-label="Cerrar menú"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Primary actions — v1.16 · point #2 añadido Biblioteca */}
            <nav className="p-3 flex flex-col gap-1.5">
              <NavRow
                icon={<Rocket className="w-4 h-4" />}
                label={t('sidebar.deployedApps')}
                onClick={() => go('/aplicaciones')}
                testid="sidebar-deployed-apps"
                primary
              />
              <NavRow
                icon={<BookMarked className="w-4 h-4" />}
                label="Biblioteca"
                onClick={() => go('/biblioteca')}
                testid="sidebar-biblioteca"
              />
            </nav>

            {/* Workspaces list */}
            <div className="px-4 pt-2 pb-1">
              <div
                className="text-[10px] tracking-[0.3em] uppercase"
                style={{ color: 'hsl(var(--text-muted))' }}
              >
                Espacios de trabajo
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-3">
              {loading ? (
                <div className="p-4 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin opacity-60" />
                </div>
              ) : workspaces && workspaces.length > 0 ? (
                <ul className="flex flex-col gap-1" data-testid="sidebar-ws-list">
                  {workspaces.slice(0, 20).map((w) => (
                    <li key={w.id} className="relative">
                      <div className="w-full flex items-stretch group">
                        <button
                          onClick={() => go(`/workspace/${w.id}`)}
                          className="flex-1 text-left px-3 py-2.5 rounded-l-xl hover:bg-white/5 transition min-w-0"
                          data-testid={`sidebar-ws-${w.id}`}
                        >
                          <div className="min-w-0">
                            <div
                              className="text-sm font-medium truncate"
                              style={{ color: 'hsl(var(--text))' }}
                            >
                              {w.name}
                            </div>
                            <div
                              className="text-[11px] mt-0.5 truncate"
                              style={{ color: 'hsl(var(--text-muted))' }}
                            >
                              Actualizado {relativeTime(w.last_activity)}
                              {w.status === 'archived' && (
                                <span className="ml-2 px-1.5 py-0.5 rounded-md text-[9px] uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                  archivado
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenu(openMenu === w.id ? null : w.id);
                          }}
                          className="px-2 rounded-r-xl hover:bg-white/5 transition opacity-40 group-hover:opacity-100"
                          aria-label="Acciones del workspace"
                          data-testid={`sidebar-ws-menu-${w.id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>

                      {openMenu === w.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenMenu(null)}
                          />
                          <div
                            className="absolute right-0 top-full mt-1 w-52 rounded-xl border shadow-xl z-20 py-1"
                            style={{
                              background: 'hsl(var(--bg))',
                              borderColor: 'hsl(var(--border))',
                            }}
                            data-testid={`sidebar-ws-actions-${w.id}`}
                          >
                            <ActionRow
                              icon={<Pencil className="w-3.5 h-3.5" />}
                              label="Renombrar"
                              testid={`ws-action-rename-${w.id}`}
                              onClick={() => {
                                setRenameValue(w.name);
                                setRenaming(w);
                                setOpenMenu(null);
                              }}
                            />
                            <ActionRow
                              icon={<Copy className="w-3.5 h-3.5" />}
                              label="Duplicar"
                              testid={`ws-action-duplicate-${w.id}`}
                              disabled={busy === 'duplicate:' + w.id}
                              onClick={async () => {
                                setBusy('duplicate:' + w.id);
                                setOpenMenu(null);
                                try {
                                  await apiService.duplicateWorkspace(w.id);
                                  toast.success('Workspace duplicado');
                                  await reloadWorkspaces();
                                } catch (err: any) {
                                  toast.error(err?.response?.data?.detail || 'No se pudo duplicar');
                                } finally { setBusy(null); }
                              }}
                            />
                            <ActionRow
                              icon={<Download className="w-3.5 h-3.5" />}
                              label="Exportar (.zip)"
                              testid={`ws-action-export-${w.id}`}
                              onClick={async () => {
                                setOpenMenu(null);
                                try {
                                  const safe = (w.name || w.id.slice(0, 8)).replace(/[^a-z0-9-_]/gi, '_');
                                  await apiService.downloadWorkspaceZip(w.id, `${safe}.zip`);
                                  toast.success('Descarga iniciada');
                                } catch (err: any) {
                                  toast.error(err?.message || 'No se pudo exportar');
                                }
                              }}
                            />
                            <ActionRow
                              icon={<Archive className="w-3.5 h-3.5" />}
                              label={w.status === 'archived' ? 'Ya está archivado' : 'Archivar'}
                              testid={`ws-action-archive-${w.id}`}
                              disabled={w.status === 'archived' || busy === 'archive:' + w.id}
                              onClick={async () => {
                                setBusy('archive:' + w.id);
                                setOpenMenu(null);
                                try {
                                  await apiService.archiveWorkspace(w.id);
                                  toast.success('Workspace archivado');
                                  await reloadWorkspaces();
                                } catch (err: any) {
                                  toast.error(err?.response?.data?.detail || 'No se pudo archivar');
                                } finally { setBusy(null); }
                              }}
                            />
                            <ActionRow
                              icon={<Trash2 className="w-3.5 h-3.5" />}
                              label="Eliminar"
                              testid={`ws-action-delete-${w.id}`}
                              danger
                              onClick={() => {
                                setConfirmDelete(w);
                                setOpenMenu(null);
                              }}
                            />
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div
                  className="p-4 text-center text-xs"
                  style={{ color: 'hsl(var(--text-muted))' }}
                >
                  No tienes workspaces todavía. Escribe tu primera idea desde el Home.
                </div>
              )}
            </div>

            {/* Footer: user + credits */}
            <div
              className="border-t p-3 flex flex-col gap-2"
              style={{ borderColor: 'hsl(var(--border))' }}
            >
              <button
                onClick={() => go('/perfil')}
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5"
                data-testid="sidebar-profile-btn"
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm"
                  style={{
                    background: 'hsl(var(--accent) / 0.15)',
                    border: '1px solid hsl(var(--accent) / 0.4)',
                    color: 'hsl(var(--accent))',
                  }}
                >
                  {(firstName[0] || 'N').toUpperCase()}
                </div>
                <div className="text-left min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" style={{ color: 'hsl(var(--text))' }}>
                    {firstName} <span className="text-[10px] opacity-60">· Mi perfil</span>
                  </div>
                  <div className="text-[11px] truncate" style={{ color: 'hsl(var(--text-muted))' }}>
                    {me?.email}
                  </div>
                </div>
              </button>
              <button
                onClick={() => { onClose(); openCreditsPanel(); }}
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5"
                data-testid="sidebar-credits-btn"
              >
                <Coins className="w-4 h-4 shrink-0" style={{ color: 'hsl(var(--accent))' }} />
                <div className="text-left min-w-0 flex-1">
                  <div className="text-sm font-medium" style={{ color: 'hsl(var(--text))' }}>
                    Créditos
                  </div>
                  <div className="text-[11px]" style={{ color: 'hsl(var(--accent))' }} data-testid="sidebar-credits-value">
                    {balance === null ? '—' : `${balance.toLocaleString('es-ES')} disponibles`}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 opacity-40" />
              </button>
              <button
                onClick={async () => {
                  await logout();
                  nav('/auth', { replace: true });
                }}
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 text-sm"
                style={{ color: 'hsl(var(--text-muted))' }}
                data-testid="sidebar-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Cerrar sesión
              </button>
            </div>
          </motion.aside>

          {/* Rename modal */}
          {renaming && (
            <motion.div
              key="rename-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] grid place-items-center bg-black/60 backdrop-blur-sm"
              onClick={() => setRenaming(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-[380px] max-w-[92vw] rounded-2xl p-6 border shadow-2xl"
                style={{ background: 'hsl(var(--bg-elev))', borderColor: 'hsl(var(--border))' }}
              >
                <h3 className="text-lg font-medium mb-3" style={{ color: 'hsl(var(--text))' }}>
                  Renombrar workspace
                </h3>
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border bg-black/20 outline-none"
                  style={{
                    borderColor: 'hsl(var(--border))',
                    color: 'hsl(var(--text))',
                  }}
                  data-testid="sidebar-rename-input"
                />
                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setRenaming(null)}
                    className="px-3 py-1.5 rounded-lg text-sm hover:bg-white/5"
                    style={{ color: 'hsl(var(--text-muted))' }}
                  >
                    Cancelar
                  </button>
                  <button
                    disabled={!renameValue.trim() || busy === 'rename:' + renaming.id}
                    onClick={async () => {
                      const name = renameValue.trim();
                      if (!name || !renaming) return;
                      setBusy('rename:' + renaming.id);
                      try {
                        await apiService.updateWorkspace(renaming.id, { name });
                        toast.success('Workspace renombrado');
                        setRenaming(null);
                        await reloadWorkspaces();
                      } catch (err: any) {
                        toast.error(err?.response?.data?.detail || 'No se pudo renombrar');
                      } finally { setBusy(null); }
                    }}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium"
                    style={{
                      background: 'hsl(var(--accent))',
                      color: 'hsl(var(--bg))',
                    }}
                    data-testid="sidebar-rename-confirm"
                  >
                    Guardar
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Delete confirm modal */}
          {confirmDelete && (
            <motion.div
              key="delete-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] grid place-items-center bg-black/60 backdrop-blur-sm"
              onClick={() => setConfirmDelete(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-[420px] max-w-[92vw] rounded-2xl p-6 border shadow-2xl"
                style={{ background: 'hsl(var(--bg-elev))', borderColor: 'hsl(var(--border))' }}
              >
                <h3 className="text-lg font-medium" style={{ color: 'hsl(var(--text))' }}>
                  Eliminar workspace
                </h3>
                <p className="text-sm mt-2 leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
                  Se eliminará <strong style={{ color: 'hsl(var(--text))' }}>{confirmDelete.name}</strong> junto
                  con su código, historial y archivos. Esta acción no
                  se puede deshacer.
                </p>
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setConfirmDelete(null)}
                    className="px-3 py-1.5 rounded-lg text-sm hover:bg-white/5"
                    style={{ color: 'hsl(var(--text-muted))' }}
                  >
                    Cancelar
                  </button>
                  <button
                    disabled={busy === 'delete:' + confirmDelete.id}
                    onClick={async () => {
                      if (!confirmDelete) return;
                      setBusy('delete:' + confirmDelete.id);
                      try {
                        await apiService.deleteWorkspace(confirmDelete.id);
                        toast.success('Workspace eliminado');
                        setConfirmDelete(null);
                        await reloadWorkspaces();
                      } catch (err: any) {
                        toast.error(err?.response?.data?.detail || 'No se pudo eliminar');
                      } finally { setBusy(null); }
                    }}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 inline-flex items-center gap-2"
                    data-testid="sidebar-delete-confirm"
                  >
                    {busy === 'delete:' + confirmDelete.id && <Loader2 className="w-4 h-4 animate-spin" />}
                    Eliminar definitivamente
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

// ── Helpers ────────────────────────────────────────────────────────
const ActionRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  testid: string;
  danger?: boolean;
  disabled?: boolean;
}> = ({ icon, label, onClick, testid, danger, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed transition"
    style={{
      color: danger ? '#f87171' : 'hsl(var(--text))',
    }}
    onMouseEnter={(e) => {
      if (!disabled) e.currentTarget.style.background = danger ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.04)';
    }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
  >
    <span style={{ color: danger ? '#f87171' : 'hsl(var(--text-muted))' }}>{icon}</span>
    {label}
  </button>
);


// ── Helpers ────────────────────────────────────────────────────────
const NavRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  testid: string;
  primary?: boolean;
}> = ({ icon, label, onClick, testid, primary }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl transition"
    style={{
      background: primary ? 'hsl(var(--accent) / 0.10)' : 'transparent',
      border: primary ? '1px solid hsl(var(--accent) / 0.30)' : '1px solid transparent',
      color: 'hsl(var(--text))',
    }}
  >
    <span className="flex items-center gap-3">
      <span style={{ color: primary ? 'hsl(var(--accent))' : 'hsl(var(--text-muted))' }}>
        {icon}
      </span>
      <span className="text-sm font-medium">{label}</span>
    </span>
    <ChevronRight className="w-4 h-4 opacity-40" />
  </button>
);

function relativeTime(iso?: string): string {
  if (!iso) return 'recientemente';
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const m = Math.round(diff / 60000);
    if (m < 1) return 'ahora';
    if (m < 60) return `hace ${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.round(h / 24);
    return `hace ${d} día${d !== 1 ? 's' : ''}`;
  } catch { return 'recientemente'; }
}
